import { z } from "zod"
import { emailSchema } from "../common/email-validator"
import { phoneNumberSchema } from "../common/phone-number-validator"
import { TranslationKey } from "../i18n/translation-key"
import { DiscountAppliesTo } from "../service/discount-code"

export enum ServiceEventStatus {
  DRAFT = "DRAFT",
  PUBLISHED = "PUBLISHED",
  COMPLETED = "COMPLETED",
  CANCELLED = "CANCELLED",
}

export const ServiceEventStatusSchema = z.nativeEnum(ServiceEventStatus)

export interface ServiceEventItem {
  id: string
  title: string
  description: string | null
  specifications: string | null
  eventId: string
}

export interface ServiceEventBooking {
  id: string
  customerName: string
  customerPhone: string | null
  customerEmail: string | null
  eventId: string
  createdAt: Date
}

export interface EventParticipant {
  firstName: string
  lastName: string | null
  email: string | null
  phoneNumber: string
}

export interface ServiceEvent {
  id: string
  title: string
  description: string | null
  imageUrl: string | null
  availableSpots: number
  phoneNumber: string | null
  address: string | null
  coordinateLat: number | null
  coordinateLon: number | null
  eventDate: Date
  startTime: string
  endTime: string
  status: ServiceEventStatus
  emailMessage: string | null
  discountPercent: number | null
  discountAppliesTo: DiscountAppliesTo | null
  discountCode: string | null
  discountExpiresAt: Date | null
  genericDiscountPercent: number | null
  genericDiscountAppliesTo: DiscountAppliesTo | null
  genericDiscountCode: string | null
  genericDiscountExpiresAt: Date | null
  internalNote: string | null
  storeId: string
  createdAt: Date
  updatedAt: Date
  deletedAt: Date | null
  serviceItems?: ServiceEventItem[]
  bookings?: ServiceEventBooking[]
  _count?: {
    jobs: number
  }
}

export const ServiceEventItemSchema = z.object({
  title: z
    .string()
    .min(1, "common.validation.headingRequired" satisfies TranslationKey),
  description: z.string().optional().nullable(),
  specifications: z.string().optional().nullable(),
})

export const CreateServiceEventRequestSchema = z.object({
  title: z
    .string()
    .min(1, "common.validation.titleRequired" satisfies TranslationKey),
  description: z.string().optional().nullable(),
  imageUrl: z.string().optional().nullable(),
  availableSpots: z
    .number()
    .int()
    .positive("common.validation.spotsMustBePositive" satisfies TranslationKey),
  phoneNumber: z
    .string()
    .min(1, "common.validation.phoneRequired" satisfies TranslationKey),
  address: z.string().optional().nullable(),
  coordinateLat: z.number().optional().nullable(),
  coordinateLon: z.number().optional().nullable(),
  eventDate: z.coerce.date<Date>(),
  startTime: z
    .string()
    .regex(
      /^\d{2}:\d{2}$/,
      "common.validation.startTimeFormat" satisfies TranslationKey,
    ),
  endTime: z
    .string()
    .regex(
      /^\d{2}:\d{2}$/,
      "common.validation.endTimeFormat" satisfies TranslationKey,
    ),
  status: ServiceEventStatusSchema.optional().default(ServiceEventStatus.DRAFT),
  serviceItems: z.array(ServiceEventItemSchema).optional(),
  emailMessage: z.string().optional().nullable(),
  discountPercent: z.number().int().min(1).max(100).optional().nullable(),
  discountAppliesTo: z.nativeEnum(DiscountAppliesTo).optional().nullable(),
  discountExpiresAt: z.coerce.date<Date>().optional().nullable(),
  genericDiscountPercent: z
    .number()
    .int()
    .min(1)
    .max(100)
    .optional()
    .nullable(),
  genericDiscountAppliesTo: z
    .nativeEnum(DiscountAppliesTo)
    .optional()
    .nullable(),
  genericDiscountExpiresAt: z.coerce.date<Date>().optional().nullable(),
})

export const UpdateServiceEventRequestSchema = z.object({
  title: z.string().min(1).optional(),
  description: z.string().optional().nullable(),
  imageUrl: z.string().optional().nullable(),
  availableSpots: z.number().int().positive().optional(),
  phoneNumber: z.string().optional().nullable(),
  address: z.string().optional().nullable(),
  coordinateLat: z.number().optional().nullable(),
  coordinateLon: z.number().optional().nullable(),
  eventDate: z.coerce.date<Date>().optional(),
  startTime: z
    .string()
    .regex(/^\d{2}:\d{2}$/)
    .optional(),
  endTime: z
    .string()
    .regex(/^\d{2}:\d{2}$/)
    .optional(),
  status: ServiceEventStatusSchema.optional(),
  serviceItems: z.array(ServiceEventItemSchema).optional(),
  emailMessage: z.string().optional().nullable(),
  discountPercent: z.number().int().min(1).max(100).optional().nullable(),
  discountAppliesTo: z.nativeEnum(DiscountAppliesTo).optional().nullable(),
  discountExpiresAt: z.coerce.date<Date>().optional().nullable(),
  genericDiscountPercent: z
    .number()
    .int()
    .min(1)
    .max(100)
    .optional()
    .nullable(),
  genericDiscountAppliesTo: z
    .nativeEnum(DiscountAppliesTo)
    .optional()
    .nullable(),
  genericDiscountExpiresAt: z.coerce.date<Date>().optional().nullable(),
  notifyParticipantsOfLocationChange: z.boolean().optional(),
})

export type CreateServiceEventRequest = z.infer<
  typeof CreateServiceEventRequestSchema
>
export type UpdateServiceEventRequest = z.infer<
  typeof UpdateServiceEventRequestSchema
>
export type ServiceEventItemInput = z.infer<typeof ServiceEventItemSchema>

export const SaveServiceEventNoteRequestSchema = z.object({
  content: z.string(),
})

export type SaveServiceEventNoteRequest = z.infer<
  typeof SaveServiceEventNoteRequestSchema
>

// Public booking types
export const BookServiceEventRequestSchema = z.object({
  firstName: z
    .string()
    .min(1, "common.validation.firstNameRequired" satisfies TranslationKey),
  lastName: z.string().optional(),
  phoneNumber: phoneNumberSchema(),
  email: emailSchema(),
  message: z.string().optional().nullable(),
  agreedToJoinStoreClub: z.boolean().default(false),
  agreedToPrivacyPolicyAndTerms: z.boolean().refine((val) => val === true, {
    message: "common.validation.acceptTermsRequired" satisfies TranslationKey,
  }),
})

export type BookServiceEventRequest = z.infer<
  typeof BookServiceEventRequestSchema
>

export interface ServiceEventPublicStore {
  id: string
  name: string
  slug: string
  customerClub: {
    name: string
    linkToInfo: string | null
  } | null
}

export interface ServiceEventPublic {
  id: string
  title: string
  description: string | null
  imageUrl: string | null
  availableSpots: number
  phoneNumber: string | null
  address: string | null
  coordinateLat: number | null
  coordinateLon: number | null
  eventDate: Date
  startTime: string
  endTime: string
  serviceItems: ServiceEventItem[]
  store: ServiceEventPublicStore
  bookingsCount: number
}

export interface BookServiceEventResponse {
  id: string
  eventId: string
  customerName: string
  jobId: string
}
